/**
 * Movement view: the board first, a text list second.
 *
 * The map is the interface — you move by tapping a tile. The list below it keeps
 * the same moves reachable for screen readers and for anyone who prefers text,
 * and it is where the movement cost is stated in words.
 */

import type { GameSession } from '@lastroll/engine';
import { KIND_STYLE } from '@lastroll/art';
import { DungeonMap } from './DungeonMap.js';
import { roomTileUri } from './art.js';
import { producedArt } from './producedArt.js';

export interface BoardProps {
  session: GameSession;
  onMove: (roomId: string) => void;
}

export function Board({ session, onMove }: BoardProps): JSX.Element {
  const options = session.moveOptions();
  const current = session.currentRoom;
  const cleared = session.state.world.clearedRooms;

  return (
    <div>
      <DungeonMap
        session={session}
        onMove={onMove}
        reachable={options.map((o) => ({ room: o.room, cost: o.cost }))}
      />

      {current && (
        <div className="panel panel--room">
          <img className="room__art" src={producedArt(current.assetId) ?? roomTileUri(current)} alt="" />
          <div className="room__text">
            <h2 className="panel__title">{current.name}</h2>
            <p className="panel__body">{current.description}</p>
          </div>
        </div>
      )}

      <div className="section-title">
        이동 ({session.movementPoints}칸) — 다 쓰지 않아도 된다
      </div>
      {options.length === 0 ? (
        <p className="muted">
          이 눈으로는 갈 수 있는 곳이 없다. 운명 빚을 내고 눈을 바꾸거나, 아래 단추로 이 눈을 흘려보내고 다시 굴린다.
        </p>
      ) : (
        <div className="board">
          {options.map((o) => (
            <button
              key={o.room}
              type="button"
              className={`room${cleared.has(o.room) ? ' is-cleared' : ''}`}
              onClick={() => onMove(o.room)}
            >
              <span className="room__name">{o.name}</span>
              <span className="room__meta">
                <span className="room__cost">{o.cost}칸</span>
                <span>{KIND_STYLE[o.kind as keyof typeof KIND_STYLE]?.label ?? o.kind}</span>
                {cleared.has(o.room) && <span>· 정리됨</span>}
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
